import styles from "./SingleSelect.module.scss";
import React, {
  PropsWithChildren,
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useState,
  useRef,
  useMemo,
  useEffect,
  CSSProperties,
  ReactElement,
} from "react";
import { Button } from "../../buttons";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useControllableState } from "../../../helpers/state/state-hooks";
import classNames from "classnames";
import { ButtonExpandable } from "../../buttons/button-expandable/button-expandable";
import { Input } from "../../input";
import { DropdownNew } from "../../dropdown-new";
import { useVirtualizer, VirtualItem } from "@tanstack/react-virtual";
import { useDebounce } from "usehooks-ts";

export type SingleSelectValue = string | number | null;

export type SingleSelectProps = {
  /** The text to display in the dropdown and filter when nothing is selected. */
  placeholder?: string;
  /** Determines if the filter is currently disabled. Defaults to false. */
  disabled?: boolean;
  /** If the filter is currently open. Defaults to false. */
  open?: boolean;
  /** Callback that is invoked when the filter's open state changes. */
  onOpenChange?: (open: boolean) => void;
  /** The currently selected value. This could be a string or null if no value is selected. */
  value?: SingleSelectValue;
  /** Callback that is invoked when the filter's selected value state changes. */
  onChange?: (value: SingleSelectValue) => void;
  /** Class names to add to the filter button. This is to extend styling (ie. make full width). */
  filterClassName?: string;
  /** Class names to add to the dropdown. This is to extend styling (ie. make full width). */
  dropdownClassName?: string;

  inputClassName?: string;

  /** Whether the component should include an input field to filter selections*/
  searchable?: boolean;

  searchValue?: string;

  /**A callback that is emitted whenever the search field value changes*/
  onSearchChange?: (value: string) => void;

  noResultsMessage?: string;

  includePortal?: boolean;

  /**Whether to sort the options alphabetically. Default true.*/
  sortOptions?: boolean;
};

type FocusOutsideEvent = CustomEvent<{
  originalEvent: FocusEvent;
}>;

export const SingleSelect = (props: PropsWithChildren<SingleSelectProps>) => {
  const [expanded, setExpanded] = useState(false);

  const [open, setOpen] = useControllableState({
    prop: props.open,
    defaultProp: false,
    onChange: props.onOpenChange,
  });

  const [selectedValue, setSelectedValue] = useControllableState({
    prop: props.value,
    defaultProp: null,
    onChange: props.onChange,
  });

  const buttonClasses = classNames(styles.button, props.filterClassName, {
    [styles.opened]: open,
    [styles.populatedLabel]: selectedValue,
    [styles.buttonWithInput]: props.searchable,
    [styles.noInput]: !props.searchable,
  });

  const inputClasses = classNames(
    styles.filterInput,
    {
      [styles.filterInputApplied]: selectedValue,
    },
    props.inputClassName,
  );

  const popoverClasses = classNames(styles.content, props.dropdownClassName, {
    [styles.popoverFullTriggerWidth]: props.searchable,
  });

  const label =
    selectedValue !== null && selectedValue !== undefined
      ? selectedValue.toString()
      : props.placeholder;

  const handleClearAll = () => {
    setSelectedValue(null);
  };

  const handleOpen = (open: boolean) => {
    if (props.disabled) return;
    setOpen(open);
    if (!open) {
      handleSearchChange("");
      setExpanded(false);
    }
  };

  useEffect(() => {
    if (parentRef.current) parentRef.current.scrollTop = 0;
  }, [open]);
  const handleFocusOutside = (e: FocusOutsideEvent) => {
    if (!open) return;
    e.preventDefault();
    if (!triggerFocus) {
      setOpen(false);
    }
  };

  const [search, setSearch] = useControllableState({
    prop: props.searchValue,
    defaultProp: "",
    onChange: props.onSearchChange,
  });

  const handleSearchChange = (value: string) => {
    if (!open) setOpen(true);
    setSearch(value);
  };

  const searchableChildren = useMemo(
    () => React.Children.toArray(props.children),
    [props.children],
  );

  const filterChildren = (children: React.ReactNode[], searchValue: string) => {
    const lowerSearchValue = searchValue.toLowerCase();
    return children.filter(
      (child) =>
        React.isValidElement<OptionProps>(child) &&
        child.props.value &&
        child.props.value.toString().toLowerCase().includes(lowerSearchValue),
    );
  };

  const debouncedSearchValue = useDebounce<string>(search, 250);

  const filteredChildren = useMemo(() => {
    return !props.searchable
      ? props.children
      : filterChildren(searchableChildren, debouncedSearchValue);
  }, [searchableChildren, debouncedSearchValue]);

  useEffect(() => {
    setOpen(false);
    setExpanded(false);
    setSearch("");
  }, [selectedValue]);

  const parentRef = React.useRef<HTMLDivElement>(null);
  const filteredChildrenArray = useMemo(
    () =>
      props.sortOptions !== undefined && !props.sortOptions
        ? React.Children.toArray(filteredChildren)
        : React.Children.toArray(filteredChildren).sort((c1, c2) => {
            if (
              (typeof c1 !== "string" && typeof c1 !== "number" && "props" in c1
                ? c1.props?.value?.toString()
                : c1) >
              (typeof c2 !== "string" && typeof c2 !== "number" && "props" in c2
                ? c2.props?.value?.toString()
                : c2)
            ) {
              return 1;
            } else if (
              (typeof c1 !== "string" && typeof c1 !== "number" && "props" in c1
                ? c1.props?.value?.toString()
                : c1) <
              (typeof c2 !== "string" && typeof c2 !== "number" && "props" in c2
                ? c2.props?.value?.toString()
                : c2)
            ) {
              return -1;
            }
            return 0;
          }),
    [filteredChildren],
  );

  const [triggerFocus, setTriggerFocus] = useState(false);

  const virtualizer = useVirtualizer({
    count: filteredChildrenArray.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40,
    overscan: 10,
  });
  const virtualItems = virtualizer.getVirtualItems();

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <SingleSelectContext.Provider
      value={[selectedValue, setSelectedValue, open, setOpen]}
    >
      <DropdownNew open={open} onOpenChange={handleOpen} modal={false}>
        <DropdownNew.Trigger asChild>
          <div className={styles.trigger} onKeyDown={handleKeyDown}>
            {props.searchable && (
              <div className={styles.inputContainer}>
                <Input
                  type="search"
                  placeholder={label}
                  disabled={props.disabled}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={() => {
                    setTriggerFocus(true);
                  }}
                  onBlur={() => {
                    setTriggerFocus(false);
                  }}
                  value={search}
                  className={inputClasses}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>
            )}
            <ButtonExpandable
              variant="secondary"
              disabled={props.disabled}
              className={buttonClasses}
              expanded={open}
              onExpandChange={setOpen}
              onFocus={() => setTriggerFocus(true)}
              onBlur={() => setTriggerFocus(false)}
            >
              {!props.searchable && (
                <span className={styles.filterLabel}>{label}</span>
              )}
            </ButtonExpandable>
          </div>
        </DropdownNew.Trigger>

        <DropdownNew.Content
          forceMount
          onOpenAutoFocus={(e) =>
            props.searchable && !expanded && e.preventDefault()
          }
          align={"start"}
          sticky={"always"}
          className={popoverClasses}
          onFocusOutside={handleFocusOutside}
          style={{
            display: open ? undefined : "none",
          }}
          aria-hidden={!open}
          includePortal={props.includePortal ?? true}
          onKeyDown={handleKeyDown}
        >
          {props.placeholder && (
            <DropdownMenu.Label className={styles.contentLabel}>
              {props.placeholder}
            </DropdownMenu.Label>
          )}
          {filteredChildrenArray.length > 0 ? (
            <div
              ref={parentRef}
              style={{
                overflowY: "auto",
                overflowX: "hidden",
                width: "100%",
              }}
              className={styles.scroll}
            >
              <div
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: "100%",
                  position: "relative",
                }}
              >
                {virtualItems &&
                  virtualItems.map((item) => {
                    const child = filteredChildrenArray[item.index];
                    return generateVirtualContent(
                      item,
                      React.isValidElement<OptionProps>(child)
                        ? child.props.value
                        : "",
                    );
                  })}
              </div>
            </div>
          ) : (
            <div className={styles.noResults}>
              {props.noResultsMessage ?? "No results found"}
            </div>
          )}
          <Delimiter />
          <div className={styles.clearButtonContainer}>
            <Button
              variant="tertiary-blue"
              disabled={selectedValue === null}
              onClick={handleClearAll}
            >
              Clear
            </Button>
          </div>
        </DropdownNew.Content>
      </DropdownNew>
    </SingleSelectContext.Provider>
  );
};

const generateVirtualContent = (
  virtualRow: VirtualItem,
  value: SingleSelectValue,
) => {
  return (
    <SingleSelect.Option
      key={virtualRow.key}
      value={value !== null ? value.toString() : ""}
      customClass={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        transform: `translateY(${virtualRow.start}px)`,
      }}
    />
  );
};

/**
 * This context is used to provide the namespaced children (ie. SingleSelect.Option) a way to fetch the
 * selected values and set the selected values. The context is an array in the similar pattern to useState would be.
 * ie. `const [selectedValues, setSelectedValues] = useContext(SingleSelectContext)`;
 */
type SingleSelectContextType = readonly [
  SingleSelectValue,
  Dispatch<SetStateAction<SingleSelectValue>>,
  boolean,
  Dispatch<SetStateAction<boolean>>,
];

const SingleSelectContext = createContext<SingleSelectContextType>([
  null,
  () => {},
  false,
  () => {},
]);

type OptionProps = {
  value: string | number;
  disabled?: boolean;
  customClass?: CSSProperties;
};

/**
 * The basic option given for a list.
 * This takes a value in it's props and allows you to specify how it's presented via children.
 * This will take care of selection behaviour.
 */
const Option = (props: PropsWithChildren<OptionProps>) => {
  const [selectedValue, setSelectedValue, open, setOpen] =
    useContext(SingleSelectContext);

  const handleSelect = () => {
    setSelectedValue(props.value);
    setOpen(false);
  };

  const classes = classNames(styles.option, {
    [styles.selectedOption]: selectedValue === props.value,
  });

  const ref = useRef<HTMLDivElement>(null);

  return (
    <DropdownNew.Item
      // ref={ref}
      className={classes}
      onSelect={() => handleSelect()}
      // tabIndex={0}
      // onKeyDown={(e) => handleItemNavigation(e, ref, handleSelect)}
      value={props.value}
      customClass={props.customClass}
    >
      {props.children ?? props.value}
    </DropdownNew.Item>
  );
};

/**
 * A basic line separator that may be used in the selection's dropdown content.
 */
export const Delimiter = () => {
  return (
    <DropdownMenu.Separator asChild>
      <div className={styles.delimiter}></div>
    </DropdownMenu.Separator>
  );
};

SingleSelect.Option = Option;
SingleSelect.Delimiter = Delimiter;
